
import React, { useState } from 'react';
import { communicationService } from '../../services/api';

interface SendReminderModalProps {
    isOpen: boolean;
    onClose: () => void;
    tenant: any; // Using any for brevity, ideally Tenant interface
}

export default function SendReminderModal({ isOpen, onClose, tenant }: SendReminderModalProps) {
    const [type, setType] = useState<'friendly' | 'firm' | 'legal'>('friendly');
    const [channel, setChannel] = useState<'email' | 'sms' | 'both'>('email');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [customMessage, setCustomMessage] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    if (!isOpen || !tenant) return null;

    const handleSend = async () => {
        setSending(true);
        try {
            const response = await communicationService.sendReminder({
                tenantId: tenant.id,
                type,
                channel,
                message: isEditing ? customMessage : getPreviewText()
            });
            setResult(response.data);
            setTimeout(() => {
                onClose();
                setResult(null);
            }, 2000);
        } catch (error: any) {
            console.error('Failed to send reminder:', error);
            alert('Failed to send reminder: ' + (error.response?.data?.message || error.message));
        } finally {
            setSending(false);
        }
    };

    const getPreviewText = () => {
        switch (type) {
            case 'friendly': return `Hi ${tenant.name}, just a friendly reminder...`;
            case 'firm': return `Dear ${tenant.name}, your rent is overdue...`;
            case 'legal': return `URGENT: Notice of Breach of Tenancy...`;
            default: return '';
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                        <i className="fas fa-paper-plane text-blue-600 text-xl"></i>
                    </div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Send Rent Reminder</h3>
                    <p className="text-sm text-gray-500 mt-2">
                        Send a payment reminder to <strong>{tenant.name}</strong>.
                    </p>

                    {/* Tone Selection */}
                    <div className="mt-4 text-left">
                        <label className="block text-sm font-medium text-gray-700">Tone</label>
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value as any)}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                            <option value="friendly">Friendly Reminder</option>
                            <option value="firm">Firm Demand</option>
                            <option value="legal">Legal Notice</option>
                        </select>
                    </div>

                    {/* Channel Selection */}
                    <div className="mt-4 text-left">
                        <label className="block text-sm font-medium text-gray-700">Channel</label>
                        <div className="mt-2 flex space-x-4">
                            <label className="inline-flex items-center">
                                <input type="radio" className="form-radio" name="channel" value="email" checked={channel === 'email'} onChange={() => setChannel('email')} />
                                <span className="ml-2">Email</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input type="radio" className="form-radio" name="channel" value="sms" checked={channel === 'sms'} onChange={() => setChannel('sms')} />
                                <span className="ml-2">SMS</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input type="radio" className="form-radio" name="channel" value="both" checked={channel === 'both'} onChange={() => setChannel('both')} />
                                <span className="ml-2">Both</span>
                            </label>
                        </div>
                    </div>

                    {/* Preview / Edit Box */}
                    <div className="mt-4 text-left">
                        <div className="flex justify-between items-center mb-1">
                            <label className="block text-sm font-medium text-gray-700">Message Content</label>
                            <button
                                onClick={() => {
                                    if (!isEditing) setCustomMessage(getPreviewText());
                                    setIsEditing(!isEditing);
                                }}
                                className="text-xs text-blue-600 font-bold hover:underline"
                            >
                                {isEditing ? 'View Default' : 'Edit Message'}
                            </button>
                        </div>

                        {isEditing ? (
                            <textarea
                                className="w-full p-3 bg-white rounded text-sm text-gray-800 border-2 border-blue-200 focus:ring-blue-500 focus:border-blue-500 h-24 font-medium"
                                value={customMessage}
                                onChange={(e) => setCustomMessage(e.target.value)}
                            />
                        ) : (
                            <div className="p-3 bg-gray-50 rounded text-sm text-gray-600 border border-gray-200 min-h-[6rem]">
                                <strong>Default Preview:</strong><br />
                                <em>"{getPreviewText()}"</em>
                            </div>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1 italic">
                            * Tokens like [name] will be replaced by the system.
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none"
                            disabled={sending}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={sending}
                            className={`px-4 py-2 ${result ? 'bg-green-600' : 'bg-blue-600'} text-white rounded-md hover:${result ? 'bg-green-700' : 'bg-blue-700'} focus:outline-none flex items-center`}
                        >
                            {sending ? (
                                <><i className="fas fa-spinner fa-spin mr-2"></i> Sending...</>
                            ) : result ? (
                                <><i className="fas fa-check mr-2"></i> Sent!</>
                            ) : (
                                <><i className="fas fa-paper-plane mr-2"></i> Send Reminder</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
